import {
    type TextStyle,
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    MarkdownEditorMeasuresConstantType,
    MarkdownEditorDynamicStyleType,
    MarkdownEditorMeasures
} from "./type";
import type {
    Mutable
} from "../../types";
import {
    getSurfaceStyle
} from "../../helpers/theme/elevation";
import {
    getInputTextStyle
} from "../../helpers/theme/input";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    getHugStyle
} from "../../helpers/theme/spread";

export const MARKDOWN_EDITOR_ROWS = 14;

export const getMarkdownEditorMeasures = ({
    typography,
    borders,
    spaces
}: MarkdownEditorMeasuresConstantType): MarkdownEditorMeasures => {
    const inputHeight = typography.monoMediumSize.lineHeight * MARKDOWN_EDITOR_ROWS;

    const controlHeight = inputHeight + (spaces.spacingSm + borders.line) * 2;

    return {
        previewHeight: controlHeight + spaces.spacingMd * 2,
        controlHeight,
        inputHeight
    };
};

const stylesheet = StyleSheet.create({
    header: {
        justifyContent: "space-between",
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap"
    },
    editorInput: {
        backgroundColor: "transparent",
        includeFontPadding: false,
        textAlignVertical: "top",
        alignSelf: "stretch",
        borderWidth: 0,
        minWidth: 0,
        padding: 0,
        margin: 0,
        flex: 1
    },
    bodyStack: {
        flexDirection: "column"
    },
    helper: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    helperKeys: {
        alignItems: "center",
        flexDirection: "row"
    },
    bodyWide: {
        flexDirection: "row"
    },
    previewContainer: {
        overflow: "hidden"
    },
    status: {
        flexShrink: 1
    },
    pane: {
        minWidth: 0,
        flex: 1
    },
    container: {
        minWidth: 0
    }
});

export const useStyles = ({
    spreadBehaviour,
    shapeConfigs,
    pointerType,
    shadowAlpha,
    elevations,
    typography,
    userStyle,
    isNarrow,
    radiuses,
    borders,
    colors,
    spaces
}: MarkdownEditorDynamicStyleType) => {
    const measures = getMarkdownEditorMeasures({
        typography,
        borders,
        spaces
    });

    const styles = {
        header: {
            borderBottomColor: colors.content.border.subtle,
            borderBottomWidth: borders.separator,
            marginBottom: -spaces.spacingMd,
            padding: spaces.spacingSm,
            gap: spaces.spacingMd
        },
        previewPane: {
            borderColor: colors.content.border.subtle,
            padding: spaces.spacingLg
        } as Mutable<ViewStyle>,
        editorInput: {
            ...getInputTextStyle({
                textStyle: typography.monoMediumSize,
                pointerType,
                typography
            }),
            height: measures.inputHeight
        } as Mutable<TextStyle>,
        previewLive: {
            height: measures.previewHeight
        },
        container: {
            ...getSurfaceStyle({
                surface: "popover",
                level: "level1",
                shadowAlpha,
                elevations,
                borders,
                colors
            }),
            ...getCornerStyle({
                radius: radiuses.surface,
                shapeConfigs
            }),
            alignSelf: "auto"
        } as Mutable<ViewStyle>,
        status: {
            paddingEnd: spaces.spacingSm
        },
        helperKeys: {
            columnGap: spaces.spacingXxs
        },
        helper: {
            columnGap: spaces.spacingXs
        },
        field: {
            padding: spaces.spacingMd,
            gap: spaces.spacingSm
        }
    };

    if(isNarrow) {
        styles.previewPane.borderTopWidth = borders.separator;
    } else {
        styles.previewPane.borderStartWidth = borders.separator;
    }

    if(spreadBehaviour === "baseline") {
        Object.assign(styles.container, getHugStyle({
            style: userStyle
        }));
    } else if(spreadBehaviour === "stretch") {
        styles.container.alignSelf = spreadBehaviour;
        styles.container.width = "100%";
    }

    return styles;
};
export default stylesheet;
